/**
 * Custom Site Specific Javascript for ECH Data Settlement
 * www.echdatasettlement.com
 *
 * Implements:
 * - Class member ID vs SSN toggle (Section II)
 * - California resident conditional fields (Section IV)
 * - Out-of-pocket expense validation ($5,000 cap) (Section III)
 */

import { handleProcessClaim } from "./customSubmitClaim";
import { createElement } from "commonAssets/lib/createElement";
import { modal } from "commonAssets/lib/modal";

/**
 * Validates a form.
 *
 * @param {Object} options - The options object.
 * @param {Event|string} options.event - The event object.
 * @param {import("./customSubmitClaim").CustomSubmitClaim} options.ctx - The current context.
 * @returns {void}
 */
export function validateForm({ event, ctx }) {
    if (event === "init_claim") {
        if (ctx.claim.is_deficient) {
            const deficientReasons = createElement(
                "ul",
                { class: "deficient-reasons-list" },
                ctx.claim.deficiency_reasons.map((reason) =>
                    createElement("li", {}, [], reason)
                )
            );

            modal(
                "Claim Deficient",
                "<p>Please fix the issues below and resubmit your claim:</p>" +
                    deficientReasons.outerHTML +
                    "<p>If you already fixed the issues, please contact the settlement administrator for assistance.</p>"
            );
        }

        // Move out of pocket expenses table beneath p.out-of-pocket-expense-lead
        setupOutOfPocketExpenseTable();

        // Move payment method note beneath payment method input
        // movePaymentMethodNote();

        // Disable first_name and last_name fields
        setReadOnlyFields("[name^='first_name'], [name^='last_name']");

        // Disable confirm email validation by syncing it to the primary email field
        disableConfirmEmailValidation();

        // ctx.supportingDocumentation.toggleVisibility(ctx.claim.has_files);
        // ctx.transactionalFields.toggleVisibility(ctx.claim.has_transactions);

        // Initialize transaction row listeners if transactional fields exist
        if (ctx.transactionalFields) {
            onTransactionRowAdded(ctx.transactionalFields.container);
        }
    }

    // Initialize California statutory cash payment toggle
    setupCaliforniaStatutoryCashPaymentToggle(ctx);

    // Make transaction row required if any of the fields are filled out
    makeTransactionRowsRequired({ ctx, event });
}

/**
 * Processes a claim for a custom member.
 *
 * @param {import("./customSubmitClaim").CustomSubmitClaim} ctx - The claim context.
 * @returns {Object} The processed claim.
 */
export function customProcessClaim(ctx) {
    const claim = handleProcessClaim.call(ctx, validateForm);
    // const hasUploadedFiles = ctx.supportingDocumentation.hasUploadedFiles;
    // const transactions = Object.values(claim.data.transactions);
    // const hasTransactions =
    //     transactions.length > 0 &&
    //     transactions.some((transaction) => transaction.Amount > 0);
    //
    // // claimant must provide supporting documentation for out-of-pocket expenses
    // if (hasTransactions && !hasUploadedFiles) {
    //     claim.flags.valid = false;
    //     modal(
    //         "Error: Missing Required Documentation",
    //         "Supporting documentation is required for Reimbursement of Out-of-Pocket Expenses."
    //     );
    //     return claim;
    // }
    //
    // // total out-of-pocket expenses must be less than or equal to $5,000
    // if (hasTransactions) {
    //     const totalAmount = transactions.reduce((acc, transaction) => {
    //         return acc + Number(transaction.Amount);
    //     }, 0);
    //
    //     if (totalAmount > 5000) {
    //         claim.flags.valid = false;
    //         modal(
    //             "Error: Total Out-of-Pocket Expenses Exceeds Limit",
    //             "The total out-of-pocket expenses must be less than or equal to $5,000."
    //         );
    //         return claim;
    //     }
    // }
    //
    return claim;
}

/**
 * Checks if a field's value is empty (trimmed).
 *
 * @param {HTMLInputElement} field - The field to check.
 * @returns {boolean} True if the field is empty.
 */
function isFieldEmpty(field) {
    return (field.value || "").trim() === "";
}

/**
 * Checks if all fields in a NodeList are empty.
 *
 * @param {NodeList} fields - The fields to check.
 * @returns {boolean} True if all fields are empty.
 */
function isRowEmpty(fields) {
    return Array.from(fields).every(isFieldEmpty);
}

/**
 * Makes the transaction rows required if any of the fields are filled out.
 * Removes required state when all fields in the row are cleared.
 *
 * @param {import("./customSubmitClaim").CustomSubmitClaim} ctx - The claim context.
 * @param {Event|string} event - The event object.
 * @returns {void}
 */
function makeTransactionRowsRequired({ ctx, event }) {
    // we can tell if this is a transactional field by checking if event.target contains "item-id" attribute.
    // this attribute refers to the row number of the transaction row.
    // we then need to find all inputs in the transaction table with that same attribute, and make them required if this input is filled out
    if (!event?.target?.hasAttribute("item-id")) return;
    const rowNumber = event.target.getAttribute("item-id");

    const rowFields = ctx.transactionalFields.container.querySelectorAll(
        `[item-id="${rowNumber}"]`
    );

    // If all fields are empty, remove required; otherwise make all required
    const shouldBeRequired = !isRowEmpty(rowFields);

    rowFields.forEach((field) => {
        toggleNodeRequired(field, shouldBeRequired);
    });
}

/**
 * Sets the read only attribute of the fields matching the selector.
 * The aria-readonly attribute is set to true to indicate the field is read only to screen readers.
 *
 * @param {string} selector - The selector to match fields.
 * @returns {void}
 */
function setReadOnlyFields(selector) {
    const fields = document.querySelectorAll(selector);
    fields.forEach((field) => {
        field.ariaReadOnly = true;
        field.readOnly = true;
    });
}

/**
 * Handles the row added event.
 *
 * @param {HTMLElement} row - The row element.
 */
export function onTransactionRowAdded(row) {
    const amountInputs = row.querySelectorAll('[name^="oop_expense_amount"]');

    amountInputs.forEach((input) => {
        input.addEventListener("blur", formatAmount);
    });
}

/**
 * Handles the row removed event.
 *
 * @param {HTMLElement} row - The row element.
 */
export function onTransactionRowRemoved(row) {
    const amountInputs = row.querySelectorAll('[name^="oop_expense_amount"]');
    amountInputs.forEach((input) => {
        input.removeEventListener("blur", formatAmount);
    });
}

/**
 * Sets up the out-of-pocket expense table.
 *
 * @returns {void}
 */
function setupOutOfPocketExpenseTable() {
    const adjacentElement = document.querySelector(
        '[data-field-id="__wrapper_2"]'
    );
    const outOfPocketExpenseTable = document.querySelector(
        "table[data-table-name='Out-of-Pocket Expenses']"
    );
    const tableActions = outOfPocketExpenseTable.parentElement.nextSibling;
    adjacentElement.parentNode.insertBefore(
        tableActions,
        adjacentElement.nextSibling
    );
    adjacentElement.parentNode.insertBefore(
        outOfPocketExpenseTable.parentElement,
        tableActions
    );
}

// /**
//  * Append the payment method note to the payment method fieldset.
//  *
//  * @returns {void}
//  */
// function movePaymentMethodNote() {
//     const paymentMethodNote = document.querySelector(
//         '[data-field-id="_payment_method_note"]'
//     );
//     const paymentMethod = document.querySelector("#payment-widget");
//     paymentMethod.appendChild(paymentMethodNote);
// }

/**
 * Converts date fields to month and year inputs.
 *
 * @param {string} selector - The selector to match nodes.
 * @returns {void}
 */
function convertDateToMonthInput(selector) {
    const containers = document.querySelectorAll(selector);
    containers.forEach((container) => {
        const dateFields = container.querySelectorAll("input[type='date']");
        if (!dateFields) return;
        dateFields.forEach((field) => {
            field.type = "month";
        });
    });
}

/**
 * Sets up the California statutory cash payment toggle.
 *
 * @param {import("./customSubmitClaim").CustomSubmitClaim} ctx - The claim context.
 * @returns {void}
 */
function setupCaliforniaStatutoryCashPaymentToggle(ctx) {
    const californiaStatutoryCashPayment = ctx.getFormInput(
        "california_statutory_cash_payment"
    );

    const attestation = ctx.getFormInput(
        "california_statutory_cash_payment_attestation"
    );

    const additionalNodes =
        '[data-field-id*="california_statutory_cash_payment_"]';

    // require attestation if californiaStatutoryCashPayment is checked
    toggleNodeRequired(attestation, californiaStatutoryCashPayment.checked);

    if (californiaStatutoryCashPayment.checked) {
        showAllNodes(additionalNodes);
        convertDateToMonthInput(additionalNodes);
    } else {
        hideAllNodes(additionalNodes);
    }
}

/**
 * Hides all nodes matching the selector.
 *
 * @param {string} selector - The selector to match nodes.
 * @returns {void}
 */
function hideAllNodes(selector) {
    const nodeList = document.querySelectorAll(selector);
    nodeList.forEach((node) => {
        toggleNodeVisibility(node);
    });
}

/**
 * Shows all nodes matching the selector.
 *
 * @param {string} selector - The selector to match nodes.
 * @returns {void}
 */
function showAllNodes(selector) {
    const nodeList = document.querySelectorAll(selector);
    nodeList.forEach((node) => {
        toggleNodeVisibility(node, true);
    });
}

/**
 * Toggles the required attribute of a node.
 *
 * @param {HTMLElement} node - The node to toggle the required attribute of.
 * @param {boolean} isRequired - Whether the node should be required.
 * @returns {void}
 */
function toggleNodeRequired(node, isRequired) {
    if (!node) return;
    node.required = isRequired;

    // Update data-validate for custom validation system
    let validation = node.getAttribute("data-validate") || "";
    if (isRequired && !validation.includes("required")) {
        validation = ("required " + validation).trim();
    } else if (!isRequired) {
        validation = validation.replace(/\brequired\b/g, "").trim();
    }
    node.setAttribute("data-validate", validation);
}

/**
 * Formats an amount input to 2 decimal places on blur.
 *
 * @param {Event} event - The event object.
 * @returns {void}
 */
function formatAmount(event) {
    const input = event.target;
    const value = input.value;

    // If the value is empty or not a valid number, clear it or leave it
    if (value === "" || isNaN(parseFloat(value))) {
        input.value = "";
        return;
    }

    // Format to 2 decimal places
    const numericValue = parseFloat(value);
    input.value = numericValue.toFixed(2);
}

/**
 * Toggles the visibility of a node.
 *
 * @param {HTMLElement} node - The node to toggle the visibility of.
 * @param {boolean} isVisible - Whether the node should be visible.
 * @returns {void}
 */
function toggleNodeVisibility(node, isVisible = false) {
    if (!node) return;
    node.classList.toggle("d-none", !isVisible);
}

/**
 * Disables confirm email matching while keeping the field optional.
 *
 * @returns {void}
 */
function disableConfirmEmailValidation() {
    const confirmEmailField = document.getElementById("confirm_email_address");

    if (!confirmEmailField) return;

    const clonedField = confirmEmailField.cloneNode(true);
    clonedField.id = "confirm_email_address_optional";
    clonedField.required = false;
    clonedField.setAttribute("data-validate", "email");
    clonedField.classList.remove("is-invalid");
    clonedField.setCustomValidity("");

    confirmEmailField.replaceWith(clonedField);

    const confirmEmailLabel = document.querySelector(
        'label[for="confirm_email_address"]'
    );
    confirmEmailLabel?.setAttribute("for", clonedField.id);
}
